/**
 * SAMPLE DATA - NOT REAL RECORDS.
 *
 * Every person named here is a fictional placeholder. Engagement rates,
 * health scores, revenue figures and review dates are illustrative and do
 * not describe any actual community, board member, or account.
 */

import type {
  PlatformAnalytics as PlatformAnalyticsType,
  BillingAnalytics,
  PlatformOverview,
} from '@/types/entities/admin/admin';

export interface SubpanelElectPlatformMetrics {
  totalCommunities: number;
  activeCommunities: number;
  totalUsers: number;
  activeResidents: number;
  monthlyAssessmentRevenue: number;
  platformSubscriptionRevenue: number;
  treasuryBalance: number;
  systemUptime: number;
  supportTickets: number;
  implementationsInFlight: number;
  systemHealthLabel: string;
}

export interface SubpanelElectActivityItem {
  id: string;
  type: 'community' | 'support' | 'system' | 'revenue' | 'governance';
  title: string;
  time: string;
  status: 'completed' | 'scheduled' | 'pending';
}

export interface SubpanelElectAlertItem {
  id: string;
  title: string;
  type: 'warning' | 'security' | 'info';
  severity: 'high' | 'medium' | 'low';
  actionLabel?: string;
}

export interface CommunitySegmentSummary {
  id: string;
  name: string;
  households: number;
  activeAccounts: number;
  engagementRate: number;
  boardLiaisons: string[];
  lastReview: string;
}

export interface AssessmentCollectionWindow {
  id: string;
  label: string;
  dueDate: string;
  amountPerParcel: number;
  expectedCollection: number;
  status: 'complete' | 'in_progress' | 'upcoming';
}

export interface EngagementCheckpoint {
  month: string;
  dailyActiveUsers: number;
  monthlyActiveUsers: number;
  sessionDurationMinutes: number;
  boardActions: number;
}

export interface GAVLiaisonWorkflowItem {
  id: string;
  title: string;
  category: 'voting_rights' | 'assessment' | 'access' | 'governance' | 'communication';
  status: 'pending' | 'in_progress' | 'resolved' | 'escalated';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignedTo: string;
  createdAt: string;
  dueDate: string;
  description: string;
  resolutionNotes?: string;
  affectedHouseholds: number;
}

export interface WinterReadinessMetric {
  id: string;
  category: 'equipment' | 'staffing' | 'budget' | 'coverage' | 'communication';
  metric: string;
  status: 'complete' | 'in_progress' | 'at_risk' | 'blocked';
  progress: number;
  targetDate: string;
  owner: string;
  notes: string;
}

export const GLASTONBURY_PLATFORM_ID = 'tenant-glastonbury';
export const GLASTONBURY_COMMUNITY_NAME = 'Community Landowners Association';

export const glastonburyPlatformMetrics: SubpanelElectPlatformMetrics = {
  totalCommunities: 1,
  activeCommunities: 1,
  totalUsers: 612,
  activeResidents: 458,
  monthlyAssessmentRevenue: 16727.2,
  platformSubscriptionRevenue: 3248,
  treasuryBalance: 200000,
  systemUptime: 99.97,
  supportTickets: 4,
  implementationsInFlight: 1,
  systemHealthLabel: 'Stable with winter readiness focus',
};

export const glastonburyPlatformActivities: SubpanelElectActivityItem[] = [
  {
    id: 'activity-001',
    type: 'community',
    title: 'Board approved winter road maintenance package for North Glastonbury',
    time: 'Updated Oct 18, 2025',
    status: 'completed',
  },
  {
    id: 'activity-002',
    type: 'governance',
    title: 'Election ballot packets mailed to 406 landowners',
    time: 'Updated Sep 22, 2025',
    status: 'completed',
  },
  {
    id: 'activity-003',
    type: 'support',
    title: 'Golden Age Village liaison review meeting scheduled',
    time: 'Scheduled Nov 2, 2025',
    status: 'scheduled',
  },
  {
    id: 'activity-004',
    type: 'revenue',
    title: 'Assessment collection pace 92% for FY2025 cycle',
    time: 'Updated Oct 15, 2025',
    status: 'completed',
  },
  {
    id: 'activity-005',
    type: 'governance',
    title: 'Emergency winter access dispute escalated to SubPanel Elect',
    time: 'Updated Oct 23, 2025',
    status: 'pending',
  },
  {
    id: 'activity-006',
    type: 'community',
    title: 'Reserve study gap analysis initiated for FY2026 capital planning',
    time: 'Scheduled Oct 30, 2025',
    status: 'scheduled',
  },
  {
    id: 'activity-007',
    type: 'governance',
    title: 'Golden Age Village voting rights documentation published',
    time: 'Updated Oct 12, 2025',
    status: 'completed',
  },
  {
    id: 'activity-008',
    type: 'system',
    title: 'Absentee ballot tracking module enabled in resident portal',
    time: 'Scheduled Nov 1, 2025',
    status: 'scheduled',
  },
  {
    id: 'activity-009',
    type: 'community',
    title: 'Park County coordination meeting on winter road maintenance',
    time: 'Updated Oct 20, 2025',
    status: 'completed',
  },
  {
    id: 'activity-010',
    type: 'revenue',
    title: 'Late payment recovery process initiated for 24 parcels',
    time: 'Updated Oct 8, 2025',
    status: 'completed',
  },
];

export const glastonburyPlatformAlerts: SubpanelElectAlertItem[] = [
  {
    id: 'alert-001',
    title: 'Winter operations vendor coverage verification required',
    type: 'warning',
    severity: 'high',
    actionLabel: 'Review snow plan',
  },
  {
    id: 'alert-002',
    title: 'Security patch released for resident portal document access',
    type: 'security',
    severity: 'medium',
    actionLabel: 'Deploy patch',
  },
  {
    id: 'alert-003',
    title: 'GAV assessment reconciliation pending board confirmation',
    type: 'info',
    severity: 'low',
    actionLabel: 'Track follow-up',
  },
  {
    id: 'alert-004',
    title: 'Reserve study adoption deadline approaching (Dec 15, 2025)',
    type: 'warning',
    severity: 'medium',
    actionLabel: 'Schedule review',
  },
  {
    id: 'alert-005',
    title: 'Election turnout projections below target for November 2025',
    type: 'info',
    severity: 'medium',
    actionLabel: 'Launch campaign',
  },
  {
    id: 'alert-006',
    title: 'Montana HOA statute compliance notice required for winter ops',
    type: 'warning',
    severity: 'high',
    actionLabel: 'Issue notice',
  },
];

export const glastonburyCommunitySegments: CommunitySegmentSummary[] = [
  {
    id: 'segment-north',
    name: 'North Glastonbury Directors Cohort',
    households: 214,
    activeAccounts: 182,
    engagementRate: 0.86,
    boardLiaisons: ['Sample Director C', 'Sample Director B'],
    lastReview: '2025-10-12T10:00:00Z',
  },
  {
    id: 'segment-south',
    name: 'South Glastonbury Directors Cohort',
    households: 168,
    activeAccounts: 143,
    engagementRate: 0.82,
    boardLiaisons: ['Sample Director A', 'Sample Finance Lead'],
    lastReview: '2025-10-14T09:15:00Z',
  },
  {
    id: 'segment-gav',
    name: 'Golden Age Village Association Liaison',
    households: 24,
    activeAccounts: 19,
    engagementRate: 0.79,
    boardLiaisons: ['Sample Governance Lead'],
    lastReview: '2025-10-08T17:18:55Z',
  },
];

export const glastonburyAssessmentSchedule: AssessmentCollectionWindow[] = [
  {
    id: 'fy25-main',
    label: 'FY2025 Assessments',
    dueDate: '2025-03-31',
    amountPerParcel: 494.4,
    expectedCollection: 200726.4,
    status: 'complete',
  },
  {
    id: 'fy25-late',
    label: 'Late Payment Recovery',
    dueDate: '2025-06-30',
    amountPerParcel: 61.8,
    expectedCollection: 25000,
    status: 'in_progress',
  },
  {
    id: 'fy26-prep',
    label: 'FY2026 Pre-Billing Checks',
    dueDate: '2025-12-15',
    amountPerParcel: 0,
    expectedCollection: 0,
    status: 'upcoming',
  },
];

export const glastonburyEngagementTimeline: EngagementCheckpoint[] = [
  {
    month: '2025-03',
    dailyActiveUsers: 162,
    monthlyActiveUsers: 374,
    sessionDurationMinutes: 16,
    boardActions: 9,
  },
  {
    month: '2025-04',
    dailyActiveUsers: 178,
    monthlyActiveUsers: 396,
    sessionDurationMinutes: 17,
    boardActions: 11,
  },
  {
    month: '2025-05',
    dailyActiveUsers: 189,
    monthlyActiveUsers: 402,
    sessionDurationMinutes: 18,
    boardActions: 10,
  },
  {
    month: '2025-06',
    dailyActiveUsers: 201,
    monthlyActiveUsers: 418,
    sessionDurationMinutes: 19,
    boardActions: 12,
  },
  {
    month: '2025-07',
    dailyActiveUsers: 214,
    monthlyActiveUsers: 431,
    sessionDurationMinutes: 21,
    boardActions: 13,
  },
  {
    month: '2025-08',
    dailyActiveUsers: 229,
    monthlyActiveUsers: 447,
    sessionDurationMinutes: 22,
    boardActions: 14,
  },
  {
    month: '2025-09',
    dailyActiveUsers: 241,
    monthlyActiveUsers: 456,
    sessionDurationMinutes: 23,
    boardActions: 15,
  },
  {
    month: '2025-10',
    dailyActiveUsers: 248,
    monthlyActiveUsers: 458,
    sessionDurationMinutes: 23,
    boardActions: 12,
  },
];

export const glastonburyPlatformOverview: PlatformOverview = {
  totalTenants: 1,
  activeTenants: 1,
  totalUsers: 612,
  activeUsers: 458,
  totalRevenue: 200726.4,
  monthlyRevenue: 16727.2,
  systemHealth: 94.5,
  uptime: 99.97,
};

export const glastonburyBillingAnalytics: BillingAnalytics = {
  revenue: {
    total: 200726.4,
    monthly: 16727.2,
    yearly: 200726.4,
    growth: 5.0,
  },
  subscriptions: {
    active: 406, // Total parcels paying annual dues
    trial: 0,
    canceled: 0,
    pastDue: 8, // Overdue parcels
  },
  churn: {
    rate: 1.8,
    reasons: [
      { reason: 'Payment plan requests', count: 5, percentage: 45 },
      { reason: 'Assessment disputes', count: 3, percentage: 27 },
      { reason: 'Seasonal vacancy', count: 2, percentage: 18 },
      { reason: 'Property sale in progress', count: 1, percentage: 10 },
    ],
  },
  forecasts: {
    nextMonthRevenue: 16727.2,
    nextQuarterRevenue: 50181.6,
    yearEndRevenue: 210762.72,
  },
};

export const glastonburyPlatformAnalytics: PlatformAnalyticsType = {
  overview: glastonburyPlatformOverview,
  tenantMetrics: {
    bySubscriptionTier: [
      { tier: 'premium', count: 1, revenue: 200726.4, percentage: 100 },
      { tier: 'basic', count: 0, revenue: 0, percentage: 0 },
      { tier: 'enterprise', count: 0, revenue: 0, percentage: 0 },
    ],
    byStatus: [
      { status: 'active', count: 1, percentage: 100 },
      { status: 'inactive', count: 0, percentage: 0 },
      { status: 'suspended', count: 0, percentage: 0 },
    ],
    growth: [
      { period: '2025-06', newTenants: 0, churnedTenants: 0, netGrowth: 0 },
      { period: '2025-07', newTenants: 0, churnedTenants: 0, netGrowth: 0 },
      { period: '2025-08', newTenants: 0, churnedTenants: 0, netGrowth: 0 },
      { period: '2025-09', newTenants: 0, churnedTenants: 0, netGrowth: 0 },
    ],
    topTenants: [
      {
        tenant: {
          id: GLASTONBURY_PLATFORM_ID,
          name: GLASTONBURY_COMMUNITY_NAME,
          subdomain: 'glastonbury',
          customDomain: 'glamontana.org',
          primaryColor: '#1f2937',
          accentColor: '#0f766e',
          status: 'active',
          subscriptionTier: 'premium',
          createdAt: '2025-01-12T09:00:00Z',
          updatedAt: '2025-10-18T12:00:00Z',
        },
        userCount: 458,
        revenue: 200726.4,
        activityScore: 91,
      },
    ],
  },
  userMetrics: {
    totalUsers: 612,
    activeUsers: 458,
    newUsersThisMonth: 42,
    userGrowthRate: 8.4,
    byRole: [
      { role: 'resident', count: 512, percentage: 83.7 },
      { role: 'moderator', count: 54, percentage: 8.8 },
      { role: 'admin', count: 46, percentage: 7.5 },
    ],
    engagement: glastonburyEngagementTimeline.map((entry) => ({
      period: entry.month,
      dailyActiveUsers: entry.dailyActiveUsers,
      monthlyActiveUsers: entry.monthlyActiveUsers,
      sessionDuration: entry.sessionDurationMinutes,
    })),
  },
  financialMetrics: {
    totalRevenue: 200726.4,
    monthlyRecurringRevenue: 16727.2,
    annualRecurringRevenue: 200726.4,
    averageRevenuePerTenant: 200726.4,
    churnRate: 1.8,
    lifetimeValue: 985000,
    revenueByTier: [
      { tier: 'premium', revenue: 200726.4, percentage: 100 },
      { tier: 'basic', revenue: 0, percentage: 0 },
      { tier: 'enterprise', revenue: 0, percentage: 0 },
    ],
    revenueHistory: [
      { period: '2025-06', revenue: 150545, subscriptions: 1, churn: 0 },
      { period: '2025-07', revenue: 166210, subscriptions: 1, churn: 0 },
      { period: '2025-08', revenue: 188658, subscriptions: 1, churn: 0 },
      { period: '2025-09', revenue: 192670, subscriptions: 1, churn: 0 },
      { period: '2025-10', revenue: 200726.4, subscriptions: 1, churn: 0 },
    ],
  },
  systemMetrics: {
    serverHealth: {
      cpu: 21.4,
      memory: 64.2,
      disk: 47.5,
      network: 9.8,
    },
    database: {
      connections: 74,
      queryTime: 2.1,
      size: 4.6,
      growth: 4.2,
    },
    api: {
      requestsPerMinute: 482,
      averageResponseTime: 132,
      errorRate: 0.08,
      uptime: 99.97,
    },
    storage: {
      totalUsed: 46.8,
      growth: 9.4,
      largestTenants: [
        {
          tenantId: GLASTONBURY_PLATFORM_ID,
          tenantName: GLASTONBURY_COMMUNITY_NAME,
          storageUsed: 46.8,
        },
      ],
    },
  },
  trends: {
    userGrowth: glastonburyEngagementTimeline.map((entry, index, arr) => {
      const previous = index === 0 ? entry.monthlyActiveUsers : arr[index - 1].monthlyActiveUsers;
      const change =
        index === 0
          ? 0
          : Number((((entry.monthlyActiveUsers - previous) / previous) * 100).toFixed(1));
      return {
        period: entry.month,
        users: entry.monthlyActiveUsers,
        change,
      };
    }),
    revenueGrowth: [
      { period: '2025-06', revenue: 150545, change: 4.5 },
      { period: '2025-07', revenue: 166210, change: 10.4 },
      { period: '2025-08', revenue: 188658, change: 13.5 },
      { period: '2025-09', revenue: 192670, change: 2.1 },
      { period: '2025-10', revenue: 200726.4, change: 4.2 },
    ],
    featureUsage: [
      { feature: 'Governance Hub', usage: 92, change: 7.2, adoptionRate: 88.4 },
      { feature: 'Financial Transparency', usage: 79, change: 5.9, adoptionRate: 73.1 },
      { feature: 'Document Center', usage: 83, change: 4.6, adoptionRate: 76.5 },
      { feature: 'Resident Messaging', usage: 94, change: 6.1, adoptionRate: 89.9 },
      { feature: 'Vendor Coordination', usage: 61, change: 3.4, adoptionRate: 54.0 },
    ],
    support: [
      { period: '2025-06', tickets: 9, avgResolutionTime: 5.7, satisfaction: 4.2 },
      { period: '2025-07', tickets: 6, avgResolutionTime: 4.1, satisfaction: 4.4 },
      { period: '2025-08', tickets: 5, avgResolutionTime: 3.9, satisfaction: 4.6 },
      { period: '2025-09', tickets: 4, avgResolutionTime: 3.6, satisfaction: 4.7 },
      { period: '2025-10', tickets: 4, avgResolutionTime: 3.4, satisfaction: 4.8 },
    ],
  },
};

// Golden Age Village Liaison Workflow
export const gavLiaisonWorkflows: GAVLiaisonWorkflowItem[] = [
  {
    id: 'gav-workflow-001',
    title: 'Absentee ballot request consolidation',
    category: 'voting_rights',
    status: 'in_progress',
    priority: 'high',
    assignedTo: 'Sample Governance Lead',
    createdAt: '2025-10-15T09:00:00Z',
    dueDate: '2025-11-01T17:00:00Z',
    description:
      '14 GAV residents requested absentee ballots for November 2025 election. Coordinate with governance committee to enable portal tracking.',
    affectedHouseholds: 14,
  },
  {
    id: 'gav-workflow-002',
    title: 'Winter access road priority clarification',
    category: 'access',
    status: 'resolved',
    priority: 'critical',
    assignedTo: 'Sample Governance Lead',
    createdAt: '2025-10-08T14:30:00Z',
    dueDate: '2025-10-20T17:00:00Z',
    description:
      'GAV loop roads require specialized plow equipment due to narrow access. Ensure vendor contract includes GAV-specific coverage terms.',
    resolutionNotes:
      'Integrated GAV access requirements into revised winter operations contract. Vendor confirmed specialized equipment allocation.',
    affectedHouseholds: 24,
  },
  {
    id: 'gav-workflow-003',
    title: 'Assessment allocation dispute resolution',
    category: 'assessment',
    status: 'escalated',
    priority: 'high',
    assignedTo: 'Sample Governance Lead',
    createdAt: '2025-09-28T10:15:00Z',
    dueDate: '2025-11-15T17:00:00Z',
    description:
      '3 GAV parcels disputed assessment allocation methodology for shared amenity costs. Requires SubPanel Elect guidance on CCR interpretation.',
    affectedHouseholds: 3,
  },
  {
    id: 'gav-workflow-004',
    title: 'Monthly liaison meeting notes publication',
    category: 'communication',
    status: 'pending',
    priority: 'medium',
    assignedTo: 'Sample Governance Lead',
    createdAt: '2025-10-26T08:00:00Z',
    dueDate: '2025-11-05T17:00:00Z',
    description:
      'Publish summary of November 2 liaison meeting to GAV residents via portal announcement.',
    affectedHouseholds: 24,
  },
  {
    id: 'gav-workflow-005',
    title: 'Designated voter transfer rules documentation',
    category: 'governance',
    status: 'resolved',
    priority: 'medium',
    assignedTo: 'Sample Governance Lead',
    createdAt: '2025-10-01T11:00:00Z',
    dueDate: '2025-10-12T17:00:00Z',
    description:
      'Clarify designated voter transfer rules for estate settlements in GAV. Requires governing documents subpanel interpretation.',
    resolutionNotes:
      'Published voting rights FAQ with estate transfer guidance. Board adopted interpretation on Oct 12, 2025.',
    affectedHouseholds: 24,
  },
];

// Winter Readiness Metrics
export const winterReadinessMetrics: WinterReadinessMetric[] = [
  {
    id: 'winter-001',
    category: 'equipment',
    metric: 'Snow removal equipment inspection and certification',
    status: 'complete',
    progress: 100,
    targetDate: '2025-10-15',
    owner: 'Sample SubPanel Lead',
    notes: 'All equipment inspected and certified. GAV-specific narrow plow confirmed available.',
  },
  {
    id: 'winter-002',
    category: 'staffing',
    metric: 'Emergency contact roster updated',
    status: 'complete',
    progress: 100,
    targetDate: '2025-10-20',
    owner: 'Roads Subpanel',
    notes: 'Updated 24/7 emergency contact list published to resident portal Oct 18, 2025.',
  },
  {
    id: 'winter-003',
    category: 'budget',
    metric: 'Winter operations reserve fund allocation',
    status: 'in_progress',
    progress: 75,
    targetDate: '2025-10-31',
    owner: 'Finance Committee',
    notes: '$18,000 allocated pending final board approval. Vote scheduled for Oct 30, 2025.',
  },
  {
    id: 'winter-004',
    category: 'coverage',
    metric: 'Vendor contract execution with GAV access terms',
    status: 'at_risk',
    progress: 60,
    targetDate: '2025-10-28',
    owner: 'Sample SubPanel Lead',
    notes:
      'Contract draft approved. Awaiting vendor signature and insurance certificate. Risk: delay impacts Nov 1 readiness.',
  },
  {
    id: 'winter-005',
    category: 'coverage',
    metric: 'Park County coordination agreement',
    status: 'complete',
    progress: 100,
    targetDate: '2025-10-20',
    owner: 'Roads Subpanel',
    notes:
      'Coordination meeting held Oct 20. County confirmed support for emergency access if needed.',
  },
  {
    id: 'winter-006',
    category: 'communication',
    metric: 'Winter operations notice published (Montana HOA statute 70-23-901)',
    status: 'at_risk',
    progress: 40,
    targetDate: '2025-10-27',
    owner: 'Governance Committee',
    notes:
      'Draft notice prepared. Pending legal review. Must publish by Oct 27 to meet 5-day notice requirement.',
  },
  {
    id: 'winter-007',
    category: 'communication',
    metric: 'Resident winter readiness guide distribution',
    status: 'in_progress',
    progress: 80,
    targetDate: '2025-10-30',
    owner: 'Communications Committee',
    notes:
      'Guide drafted with GAV-specific access information. Publishing to portal and email Oct 28.',
  },
  {
    id: 'winter-008',
    category: 'equipment',
    metric: 'Emergency snow removal equipment backup plan',
    status: 'complete',
    progress: 100,
    targetDate: '2025-10-22',
    owner: 'Sample SubPanel Lead',
    notes: 'Backup vendor identified (Park County contractor). MOU signed for emergency coverage.',
  },
];

export const glastonburyBillingEvents = {
  invoices: [
    {
      id: 'invoice-fy25-main',
      tenantId: GLASTONBURY_PLATFORM_ID,
      subscriptionId: 'sub-glastonbury-2025',
      amount: 200726.4,
      currency: 'USD',
      status: 'paid' as const,
      dueDate: '2025-03-31T00:00:00Z',
      paidAt: '2025-03-21T12:00:00Z',
      items: [
        {
          id: 'line-assessment-2025',
          description: 'FY2025 Annual Assessment Billing',
          amount: 200726.4,
          quantity: 1,
          period: {
            start: '2025-01-01T00:00:00Z',
            end: '2025-12-31T23:59:59Z',
          },
        },
      ],
      createdAt: '2025-01-15T09:00:00Z',
      updatedAt: '2025-03-21T12:00:00Z',
    },
    {
      id: 'invoice-winter-ops',
      tenantId: GLASTONBURY_PLATFORM_ID,
      subscriptionId: 'sub-glastonbury-2025',
      amount: 18000,
      currency: 'USD',
      status: 'open' as const,
      dueDate: '2025-11-30T00:00:00Z',
      items: [
        {
          id: 'line-snow-ops',
          description: 'Winter Road Operations Fund (Seasonal)',
          amount: 18000,
          quantity: 1,
          period: {
            start: '2025-11-01T00:00:00Z',
            end: '2026-03-31T23:59:59Z',
          },
        },
      ],
      createdAt: '2025-09-15T10:00:00Z',
      updatedAt: '2025-09-15T10:00:00Z',
    },
    {
      id: 'invoice-gav-reconciliation',
      tenantId: GLASTONBURY_PLATFORM_ID,
      subscriptionId: 'sub-glastonbury-2025',
      amount: 5200,
      currency: 'USD',
      status: 'open' as const,
      dueDate: '2025-12-31T00:00:00Z',
      items: [
        {
          id: 'line-gav',
          description: 'Golden Age Village Associate Assessments',
          amount: 5200,
          quantity: 1,
          period: {
            start: '2025-09-01T00:00:00Z',
            end: '2025-12-31T23:59:59Z',
          },
        },
      ],
      createdAt: '2025-09-20T08:30:00Z',
      updatedAt: '2025-09-20T08:30:00Z',
    },
  ],
  subscriptions: [
    {
      id: 'sub-glastonbury-2025',
      tenantId: GLASTONBURY_PLATFORM_ID,
      plan: {
        id: 'governance-premium',
        name: 'Governance Premium',
        tier: 'premium',
        price: 3248,
        currency: 'USD',
        interval: 'month',
        features: [],
        limits: {
          maxUsers: 800,
          maxStorage: 120,
          maxModules: 12,
          maxCustomization: true,
          prioritySupport: true,
          apiCallsPerMonth: 20000,
        },
        isActive: true,
        createdAt: '2025-01-12T09:00:00Z',
        updatedAt: '2025-10-18T12:00:00Z',
      },
      status: 'active' as const,
      currentPeriodStart: '2025-10-01T00:00:00Z',
      currentPeriodEnd: '2025-10-31T23:59:59Z',
      cancelAtPeriodEnd: false,
      createdAt: '2025-01-12T09:00:00Z',
      updatedAt: '2025-10-01T00:00:00Z',
    },
  ],
};
